import {
  type RequestBody,
  captureSentryError,
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { showWarnNotification } from '@vrplatform/theme';
import { bankersRound, day, type Maybe } from '@vrplatform/utils';
import type { party_enum } from '@vrplatform/graphql';
import { useCallback } from 'react';
import { buildReservationAdjustmentEditMutationBody } from './useReservationAdjustmentMutation.utils';

export type ReservationAdjustmentAddInputs = {
  description: string;
  lineType: string;
  centTotal?: number;
  party: party_enum;
  postingAt: Maybe<string>;
};

export function useReservationAdjustmentMutation(
  reservationId: string | null,
  onSuccess: () => void
) {
  const invalidate = useInvalidateApiQueries();

  const {
    mutateAsync: updateReservation,
    isPending: loadingUpdateReservation,
  } = useApiMutation('put', '/reservations/{id}');
  const { mutateAsync: updateAdjustment, isPending: loadingUpdateAdjustment } =
    useApiMutation('put', '/reservations/{reservationId}/adjustments/{id}');

  const { mutateAsync: deleteAdjustment, isPending: loadingDeleteAdjustment } =
    useApiMutation('delete', '/reservations/{reservationId}/adjustments/{id}');

  const { mutateAsync: createAdjustment, isPending: loadingCreateAdjustment } =
    useApiMutation('post', '/reservations/{reservationId}/adjustments');

  const submit = useCallback(
    async (
      line_type: 'adjustment' | 'financial',
      args:
        | {
            data:
              | ReservationAdjustmentAddInputs
              | ReservationAdjustmentAddInputs[];
            type: 'add';
          }
        | {
            data: {
              id: string;
            };
            type: 'remove';
          }
        | {
            data: ReservationAdjustmentAddInputs;
            lineId: string;
            type: 'edit';
          }
    ) => {
      if (!reservationId) {
        captureSentryError('Missing reservationId');
        return showWarnNotification({
          title: 'Missing reservation',
          message: 'Please reach out to support if the issue persists.',
        });
      }

      try {
        if (args.type === 'edit') {
          await updateAdjustment({
            params: {
              path: {
                reservationId,
                id: args.lineId,
              },
            },
            body: buildReservationAdjustmentEditMutationBody({
              description: args.data.description,
              centTotal: args.data.centTotal,
              postingAt: args.data.postingAt,
            }),
          });

          invalidate(['reservations']);
          onSuccess();
          return;
        }

        const formatInputs = (input: ReservationAdjustmentAddInputs) =>
          ({
            description: input.description.trim() || '-',
            type: input.lineType,
            amount: bankersRound(input.centTotal ?? 0),
            party: input.party ?? 'owners',
            generalLedgerPostingAt: input.postingAt
              ? day(input.postingAt).yyyymmdd()
              : undefined,
          }) satisfies RequestBody<'post:/reservations/{reservationId}/adjustments'>;

        const _create =
          args.type === 'add'
            ? Array.isArray(args.data)
              ? args.data.map(formatInputs)
              : [formatInputs(args.data)]
            : undefined;

        const _delete = args.type === 'remove' ? [args.data.id] : undefined;

        if (line_type === 'financial') {
          await updateReservation({
            params: {
              path: {
                id: reservationId,
              },
            },
            body: {
              lines_create: _create,
              lines_delete: _delete,
            },
          });
        } else {
          // Delete adjustments
          if (_delete?.length) {
            await Promise.all(
              _delete.map(async (id) => {
                await deleteAdjustment({
                  params: {
                    path: { reservationId, id },
                  },
                });
              })
            );
          }
          // Add adjustments
          if (_create?.length) {
            await Promise.all(
              _create.map(async (input) => {
                await createAdjustment({
                  params: {
                    path: { reservationId },
                  },
                  body: input,
                });
              })
            );
          }
        }

        invalidate(['reservations']);
        onSuccess();
      } catch (error: any) {
        showApiErrorNotification({
          title: 'Failed to update reservation',
          defaultMessage:
            'We failed to update the reservation. Please try again later and if the problem persists, contact support.',
          error,
        });
      }
    },
    [
      reservationId,
      onSuccess,
      updateReservation,
      updateAdjustment,
      invalidate,
      deleteAdjustment,
      createAdjustment,
    ]
  );

  return {
    loading:
      loadingUpdateReservation ||
      loadingUpdateAdjustment ||
      loadingDeleteAdjustment ||
      loadingCreateAdjustment,
    submit,
  };
}
